// this is the piece that actually reaches out to the internet and 
// downloads the file's raw data, one time, tracking real download 
// progress as it comes in. It also figures out early if something's
//  wrong (expired link, blocked request, etc.) before handing 
// anything to a viewer.

import { useCallback, useEffect, useRef, useState } from 'react';
import { LoadError, classifyFetchError, classifyHttpStatus, classifyReadError } from '../utils/errorClassification';

export type LoadStatus = 'loading' | 'ready' | 'error';

interface DocumentBytesState {
  status: LoadStatus;
  bytes: ArrayBuffer | null;
  progress: number | null; // 0-100, or null while Content-Length is unknown
  error: LoadError | null;
}

const INITIAL_STATE: DocumentBytesState = { status: 'loading', bytes: null, progress: null, error: null };

// Fetches the SAS URL exactly once and caches the result as the "master"
// ArrayBuffer for the lifetime of this hook instance (see utils/bufferGuard
// for why renderers must copy rather than consume it directly).
//
// Deliberately never passes the URL itself to a renderer — see PdfRenderer's
// header comment for why: a SAS token that expires mid-session turns into a
// pdf.js "corrupt document" error days after the real cause (403 from
// storage) if the renderer is left to make its own range requests.
export function useDocumentBytes(sasUrl: string) {
  const [state, setState] = useState<DocumentBytesState>(INITIAL_STATE);

  // Guards against a stale in-flight request (e.g. sasUrl changed, or retry()
  // was called again before the first attempt finished) clobbering state that
  // belongs to a newer request.
  const requestIdRef = useRef(0);

  const load = useCallback(async (url: string) => {
    const requestId = ++requestIdRef.current;
    const isStale = () => requestId !== requestIdRef.current;

    setState(INITIAL_STATE);

    let res: Response;
    try {
      res = await fetch(url);
    } catch (err) {
      if (!isStale()) setState({ status: 'error', bytes: null, progress: null, error: classifyFetchError(err) });
      return;
    }

    if (!res.ok) {
      if (!isStale()) setState({ status: 'error', bytes: null, progress: null, error: classifyHttpStatus(res.status) });
      return;
    }

    const totalHeader = res.headers.get('content-length');
    const total = totalHeader ? parseInt(totalHeader, 10) : null;

    try {
      if (!res.body) {
        // Streaming isn't available in this environment — fall back to a
        // single await with no incremental progress.
        const buffer = await res.arrayBuffer();
        if (!isStale()) setState({ status: 'ready', bytes: buffer, progress: 100, error: null });
        return;
      }

      const reader = res.body.getReader();
      const chunks: Uint8Array[] = [];
      let loaded = 0;

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        loaded += value.byteLength;
        // Precomputed per-iteration, rather than read from `loaded` inside
        // the updater closure — `loaded` is one shared mutable binding
        // across every loop iteration, so a closure over it directly would
        // risk reading a later iteration's value if React ever deferred
        // invoking the updater.
        const nextProgress = total ? Math.min(99, Math.round((loaded / total) * 100)) : null;
        if (!isStale()) {
          setState((prev) => ({ ...prev, progress: nextProgress }));
        }
      }

      const combined = new Uint8Array(loaded);
      let offset = 0;
      for (const chunk of chunks) {
        combined.set(chunk, offset);
        offset += chunk.byteLength;
      }

      if (!isStale()) setState({ status: 'ready', bytes: combined.buffer, progress: 100, error: null });
    } catch (err) {
      if (!isStale()) setState({ status: 'error', bytes: null, progress: null, error: classifyReadError(err) });
    }
  }, []);

  useEffect(() => {
    void load(sasUrl);
  }, [sasUrl, load]);

  const retry = useCallback(() => void load(sasUrl), [sasUrl, load]);

  return { ...state, retry };
}
